// =====================================================
// BUDGET TYPES - Módulo de Presupuestos
// =====================================================

export type BudgetStatus = 'BORRADOR' | 'ENVIADO' | 'ACEPTADO' | 'RECHAZADO';

export const BUDGET_STATUS_LABELS: Record<BudgetStatus, string> = {
  BORRADOR: 'Borrador',
  ENVIADO: 'Enviado',
  ACEPTADO: 'Aceptado',
  RECHAZADO: 'Rechazado',
};

export const BUDGET_STATUS_COLORS: Record<BudgetStatus, { bg: string; text: string; border: string }> = {
  BORRADOR: { bg: 'bg-gray-100', text: 'text-gray-700', border: 'border-gray-300' },
  ENVIADO: { bg: 'bg-blue-100', text: 'text-blue-700', border: 'border-blue-300' },
  ACEPTADO: { bg: 'bg-green-100', text: 'text-green-700', border: 'border-green-300' },
  RECHAZADO: { bg: 'bg-red-100', text: 'text-red-700', border: 'border-red-300' },
};

export type PriceSource = 'act' | 'site' | 'tariff' | 'override';

export const PRICE_SOURCE_LABELS: Record<PriceSource, string> = {
  act: 'Precio base',
  site: 'Precio centro',
  tariff: 'Precio tarifa',
  override: 'Precio manual',
};

// =====================================================
// INTERFACES DE RESPUESTA
// =====================================================

export interface BudgetPatientInfo {
  id: string;
  fullName: string;
  documentType: string;
  documentNumber: string;
  email: string;
  phonePrimary: string;
  age: number;
  insuranceCompanyName: string;
}

export interface BudgetTreatmentInfo {
  id: string;
  code: string;
  name: string;
}

export interface BudgetSocietyInfo {
  isActSpecific?: boolean;
  id: string;
  code: string;
  name: string;
  isDefault: boolean;
}

export interface BudgetTariffInfo {
  id: string;
  code: string;
  name: string;
  insuranceCompanyName: string;
  policyName: string;
}

export interface BudgetRejectionOptionInfo {
  id: string;
  name: string;
  requiresNote: boolean;
}

export interface BudgetLineResponse {
  id: string;
  medicalActId: string | null;
  medicalActCode: string;
  medicalActName: string;
  requiresOdontogram: boolean;
  odontogramType: string | null;
  quantity: number;
  unitPrice: number;
  priceSource: PriceSource;
  priceSourceLabel: string;
  discountType: string | null;
  discountValue: number | null;
  discountAmount: number;
  subtotal: number;
  total: number;
  toothNumbers: string | null;
  sortOrder: number;
  society: BudgetSocietyInfo | null;
  tariff: BudgetTariffInfo | null;
}

export interface BudgetResponse {
  id: string;
  budgetNumber: string;
  version: number;
  status: BudgetStatus;
  locked: boolean;
  siteId: string;
  siteName: string;
  siteCode: string;
  patient: BudgetPatientInfo;
  treatment: BudgetTreatmentInfo | null;
  lines: BudgetLineResponse[];
  linesCount: number;
  subtotal: number;
  discountType: string | null;
  discountValue: number | null;
  discountAmount: number;
  total: number;
  internalNotes: string | null;
  publicNotes: string | null;
  rejectionOption: BudgetRejectionOptionInfo | null;
  rejectionNotes: string | null;
  parentBudgetId: string | null;
  parentBudgetNumber: string | null;
  childBudgetsCount: number;
  sentAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  signedDocumentUrl: string | null;
  paymentStatus: string | null;
  totalPaid: number;
  pendingAmount: number;
  createdAt: string;
  updatedAt: string;
  createdByName: string;
  updatedByName: string | null;
  // FIX BUG 3: Sociedades activas del centro para poblar el desplegable
  // de cada línea cuando se carga un presupuesto existente.
  siteSocieties: Array<{ id: string; code: string; name: string; isDefault: boolean }>;
  // FIX BUG 1: ID de la sociedad por defecto del centro (ya venía del backend,
  // confirmado también en activeSite tras fix de auth.service.ts)
  siteDefaultSocietyId: string | null;
}

export interface BudgetListItem {
  id: string;
  budgetNumber: string;
  version: number;
  status: BudgetStatus;
  patientId: string;
  patientName: string;
  patientDocument: string;
  treatmentName: string | null;
  linesCount: number;
  total: number;
  createdAt: string;
  sentAt: string | null;
  acceptedAt: string | null;
  rejectedAt: string | null;
  paymentStatus: string | null;
  totalPaid: number;
  pendingAmount: number;
  hasReminders?: boolean;
}

export interface PaginatedBudgets {
  data: BudgetListItem[];
  meta: {
    total: number;
    page: number;
    limit: number;
    totalPages: number;
    hasNextPage: boolean;
    hasPrevPage: boolean;
  };
}

// =====================================================
// INTERFACES DE QUERY
// =====================================================

export interface BudgetQueryParams {
  page?: number;
  limit?: number;
  search?: string;
  patientId?: string;
  status?: BudgetStatus;
  dateFrom?: string;
  dateTo?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'total' | 'budgetNumber';
  sortOrder?: 'asc' | 'desc';
  includeRectifications?: boolean;
}

export interface BudgetMedicalActsQueryParams {
  search?: string;
  specialtyId?: string;
  subspecialtyId?: string;
  treatmentGroupId?: string;
  limit?: number;
}

// =====================================================
// INTERFACES DE CREACIÓN/EDICIÓN
// =====================================================

export interface CreateBudgetLineRequest {
  medicalActId: string;
  quantity: number;
  societyId?: string;
  tariffId?: string;
  priceOverride?: number;
  discountType?: string | null;
  discountValue?: number | null;
  toothNumbers?: string;
  sortOrder?: number;
}

export interface CreateBudgetRequest {
  patientId: string;
  treatmentId?: string;
  internalNotes?: string;
  publicNotes?: string;
  discountType?: string | null;
  discountValue?: number | null;
  lines?: CreateBudgetLineRequest[];
}

export interface UpdateBudgetLineRequest extends Partial<CreateBudgetLineRequest> {
  id?: string;
  _delete?: boolean;
}

export interface UpdateBudgetRequest {
  treatmentId?: string;
  internalNotes?: string;
  publicNotes?: string;
  discountType?: string | null;
  discountValue?: number | null;
  lines?: UpdateBudgetLineRequest[];
}

export interface AddBudgetLineRequest {
  medicalActId: string;
  quantity: number;
  societyId?: string;
  tariffId?: string;
  priceOverride?: number;
  discountType?: string;
  discountValue?: number;
  toothNumbers?: string;
  sortOrder?: number;
}

export interface LoadTemplateRequest {
  templateId: string;
  replaceExisting?: boolean;
}

// =====================================================
// INTERFACES DE ACCIONES
// =====================================================

export interface SendBudgetRequest {
  sendEmail?: boolean;
  email?: string;
  printOnly?: boolean;
}

export interface AcceptBudgetRequest {
  notes?: string;
}

export interface RejectBudgetRequest {
  rejectionOptionId: string;
  rejectionNotes?: string;
}

export interface CreateRectificationRequest {
  reason?: string;
}

// =====================================================
// INTERFACES DE ACTOS MÉDICOS PARA PRESUPUESTOS
// =====================================================

export interface BudgetMedicalActSpecialty {
  id: string;
  code: string;
  name: string;
}

export interface BudgetMedicalActInfo {
  id: string;
  code: string;
  name: string;
  requiresOdontogram: boolean;
  odontogramType: string | null;
  defaultPrice: number;
  sitePrice: number | null;
  tariffPrice: number | null;
  calculatedPrice: number;
  priceSource: PriceSource;
  specialties: BudgetMedicalActSpecialty[];
  subspecialties: BudgetMedicalActSpecialty[];
  treatmentGroups: BudgetMedicalActSpecialty[];
  societies: BudgetSocietyInfo[];
}

export interface CalculatedPriceInfo {
  price: number;
  source: PriceSource;
  sourceLabel: string;
  tariffId: string | null;
  tariffName: string | null;
}

// ── Pagos de presupuesto ──────────────────────────────────────────────────────
export type PaymentMethod = 'EFECTIVO' | 'TRANSFERENCIA' | 'TARJETA' | 'FINANCIACION';
export type PaymentStatus = 'PENDIENTE' | 'PARCIAL' | 'PAGADO' | 'DEVOLUCION';

export interface BudgetPaymentLog {
  id: string;
  action: string;
  previousAmount: number | null;
  newAmount: number | null;
  previousMethod: string | null;
  newMethod: string | null;
  previousNotes: string | null;
  newNotes: string | null;
  reason: string | null;
  createdByName: string | null;
  createdAt: string;
}

export interface BudgetPayment {
  id: string;
  budgetId: string;
  amount: number;
  method: PaymentMethod;
  notes: string | null;
  status: string;
  isTransfer: boolean;
  type: string;
  fromBudgetId: string | null;
  fromBudgetNumber: string | null;
  createdById: string;
  createdByName: string | null;
  createdAt: string;
  logs: BudgetPaymentLog[];
}

export interface CreatePaymentRequest {
  amount: number;
  method: PaymentMethod;
  notes?: string;
  type?: string; // PAYMENT | REFUND
}

export interface UpdatePaymentRequest {
  amount?: number;
  method?: PaymentMethod;
  notes?: string;
  reason?: string;
}
